import React from 'react';
import { Link } from 'react-router';
import {
  STUDIO_COPY,
  STUDIO_FILMSTRIP_SQUARE_PNG,
  STUDIO_FILMSTRIP_SQUARE_SRC,
  STUDIO_STATUS_COPY,
  formatEstimatedReelTime,
  type StudioJobStatus,
} from '../config';

type StudioProgressPanelProps = {
  status: string;
  /** When true, show navigate-away affordance (job keeps running server-side). */
  allowLeave?: boolean;
  detectWindowsTotal?: number | null;
  detectWindowsDone?: number | null;
  detectWindowsFailed?: number | null;
  vodDurationSec?: number | null;
  /** Client poll ceiling hit — still in-flight, not failed. */
  overdue?: boolean;
  /** Detect Job slot full — reservation held, dispatch deferred. */
  detectQueued?: boolean;
};

export type StageState = 'pending' | 'in_progress' | 'completed';

export type PipelineStage = {
  id: 'download' | 'detect' | 'render' | 'ready';
  label: string;
  detail: string;
  state: StageState;
};

export function getPipelineStages(
  status: string,
  detectWindowsTotal?: number | null,
  detectWindowsDone?: number | null
): PipelineStage[] {
  const isPostDownload = [
    'source_ready',
    'detecting',
    'detect_ready',
    'awaiting_curation',
    'rendering',
    'render_ready',
    'delivered',
  ].includes(status);

  const isPostDetect = [
    'detect_ready',
    'awaiting_curation',
    'rendering',
    'render_ready',
    'delivered',
  ].includes(status);

  const isPostRender = status === 'delivered';

  // Stage 1: Download
  let downloadState: StageState = 'pending';
  let downloadDetail = 'Pulling tape…';
  if (isPostDownload) {
    downloadState = 'completed';
    downloadDetail = 'Tape secured';
  } else if (status === 'queued' || status === 'downloading') {
    downloadState = 'in_progress';
    downloadDetail = status === 'queued' ? 'In queue…' : 'Pulling tape…';
  }

  // Stage 2: Finding plays (detect)
  let detectState: StageState = 'pending';
  let detectDetail = 'Pending tape';
  if (isPostDetect) {
    detectState = 'completed';
    detectDetail = 'Highlights locked';
  } else if (status === 'detecting' || status === 'source_ready') {
    detectState = 'in_progress';
    if (detectWindowsTotal && detectWindowsTotal > 0) {
      const done = Math.min(detectWindowsDone ?? 0, detectWindowsTotal);
      detectDetail = `Window ${done} of ${detectWindowsTotal}`;
    } else {
      detectDetail = 'Scanning footage…';
    }
  }

  // Stage 3: Building reel (render)
  let renderState: StageState = 'pending';
  let renderDetail = 'Waiting for plays';
  if (isPostRender) {
    renderState = 'completed';
    renderDetail = 'Reel built';
  } else if (
    status === 'rendering' ||
    status === 'render_ready' ||
    status === 'awaiting_curation' ||
    status === 'detect_ready'
  ) {
    renderState = 'in_progress';
    renderDetail =
      status === 'awaiting_curation' ? 'Curating plays…' : 'Cutting reel…';
  }

  // Stage 4: Ready
  let readyState: StageState = 'pending';
  let readyDetail = 'Final export';
  if (isPostRender) {
    readyState = 'completed';
    readyDetail = 'Ready to watch';
  }

  return [
    {
      id: 'download',
      label: 'Downloading',
      detail: downloadDetail,
      state: downloadState,
    },
    {
      id: 'detect',
      label: 'Finding plays',
      detail: detectDetail,
      state: detectState,
    },
    {
      id: 'render',
      label: 'Building reel',
      detail: renderDetail,
      state: renderState,
    },
    {
      id: 'ready',
      label: 'Ready',
      detail: readyDetail,
      state: readyState,
    },
  ];
}

export function progressFor(
  status: string,
  detectWindowsTotal?: number | null,
  detectWindowsDone?: number | null
): number | null {
  if (status === 'failed') return null;
  if (status === 'queued') return 5;
  if (status === 'downloading') return 12;
  if (status === 'source_ready') return 20;
  if (status === 'detecting') {
    if (detectWindowsTotal && detectWindowsTotal > 0) {
      const done = Math.max(0, detectWindowsDone ?? 0);
      const frac = Math.min(1, done / detectWindowsTotal);
      return Math.round(20 + frac * 55); // 20% -> 75%
    }
    return 45;
  }
  if (status === 'detect_ready') return 75;
  if (status === 'awaiting_curation') return 78;
  if (status === 'rendering') return 88;
  if (status === 'render_ready') return 95;
  if (status === 'delivered') return 100;
  return 5;
}

export function copyFor(
  status: string,
  detectWindowsTotal?: number | null,
  detectWindowsDone?: number | null,
  detectQueued?: boolean
): string {
  if (detectQueued && (status === 'source_ready' || status === 'queued')) {
    return STUDIO_COPY.detectQueued;
  }
  if (
    (status === 'detecting' || status === 'source_ready') &&
    detectWindowsTotal &&
    detectWindowsTotal > 0
  ) {
    const done = Math.min(detectWindowsDone ?? 0, detectWindowsTotal);
    return `Finding plays (window ${done} of ${detectWindowsTotal})…`;
  }
  if (status === 'detecting' || status === 'source_ready') {
    return 'Finding plays…';
  }
  if (status in STUDIO_STATUS_COPY) {
    const key = status as StudioJobStatus;
    if (key === 'delivered') return STUDIO_STATUS_COPY.rendering;
    return STUDIO_STATUS_COPY[key];
  }
  return 'Working on your reel…';
}

const StudioProgressPanel: React.FC<StudioProgressPanelProps> = ({
  status,
  allowLeave = false,
  detectWindowsTotal,
  detectWindowsDone,
  vodDurationSec,
  overdue = false,
  detectQueued = false,
}) => {
  const pct = progressFor(status, detectWindowsTotal, detectWindowsDone);
  const copy = copyFor(
    status,
    detectWindowsTotal,
    detectWindowsDone,
    detectQueued
  );
  const stages = getPipelineStages(status, detectWindowsTotal, detectWindowsDone);
  const etaCopy = formatEstimatedReelTime(vodDurationSec);

  return (
    <div
      className="space-y-8 text-center py-10 px-4"
      data-testid="studio-progress"
    >
      <div className="studio-tape-stripe rounded-sm opacity-70" aria-hidden />

      <div className="studio-progress-badge-wrap" data-testid="studio-progress-badge">
        <picture>
          <source type="image/webp" srcSet={STUDIO_FILMSTRIP_SQUARE_SRC} />
          <img
            src={STUDIO_FILMSTRIP_SQUARE_PNG}
            alt="Scoutlytics Studio"
            className="studio-progress-badge"
            width={180}
            height={180}
          />
        </picture>
      </div>

      <div className="space-y-2">
        <h1
          className="text-2xl md:text-3xl font-semibold tracking-tight"
          data-testid="studio-progress-copy"
        >
          {copy}
        </h1>
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-[var(--studio-accent-bright)]"
          data-testid="studio-progress-eta"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--studio-accent-bright)] animate-pulse" />
          <span>Estimated time: {etaCopy}</span>
        </div>
      </div>

      {/* 4-Stage Live Pipeline Tracker */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-2 text-left"
        data-testid="studio-pipeline-stages"
      >
        {stages.map((stage) => {
          const isCompleted = stage.state === 'completed';
          const isInProgress = stage.state === 'in_progress';

          return (
            <div
              key={stage.id}
              data-testid={`studio-stage-${stage.id}`}
              data-stage-state={stage.state}
              className={`p-3 rounded-lg border transition-all ${
                isInProgress
                  ? 'bg-purple-950/40 border-[var(--studio-accent)] ring-1 ring-[var(--studio-accent)]/50'
                  : isCompleted
                  ? 'bg-white/[0.03] border-white/15'
                  : 'bg-white/[0.01] border-white/5 opacity-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold shrink-0 ${
                    isCompleted
                      ? 'bg-[var(--studio-accent)] text-white'
                      : isInProgress
                      ? 'border border-[var(--studio-accent-bright)] text-[var(--studio-accent-bright)] animate-pulse'
                      : 'border border-white/20 text-white/40'
                  }`}
                  aria-hidden
                >
                  {isCompleted ? '✓' : ''}
                </span>
                <p className="text-xs font-medium truncate text-white">
                  {stage.label}
                </p>
              </div>
              <p
                className="text-[11px] text-[var(--studio-muted)] truncate pl-7"
                data-testid={`studio-stage-detail-${stage.id}`}
              >
                {stage.detail}
              </p>
            </div>
          );
        })}
      </div>

      {/* Smooth Progress Bar */}
      {pct !== null ? (
        <div className="space-y-1.5">
          <div
            className="studio-progress-track"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="studio-progress-bar"
          >
            <div
              className="studio-progress-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[11px] text-[var(--studio-muted)] text-right">
            {pct}%
          </p>
        </div>
      ) : null}

      <div className="flex justify-center">
        <div className="studio-spinner" aria-hidden />
      </div>

      {overdue ? (
        <p
          className="studio-prose text-sm text-[var(--studio-accent-bright)] leading-relaxed"
          data-testid="studio-progress-still-processing"
          role="status"
        >
          {STUDIO_COPY.pollStillProcessing}
        </p>
      ) : null}

      <p
        className="studio-prose text-sm text-[var(--studio-muted)] leading-relaxed"
        data-testid="studio-progress-reassurance"
      >
        {STUDIO_COPY.progressReassurance}
      </p>

      {allowLeave ? (
        <div
          className="space-y-3 pt-4 border-t border-white/5"
          data-testid="studio-progress-leave"
        >
          <p className="text-sm text-[var(--studio-muted)]">
            {STUDIO_COPY.reelsLeaveHint}
          </p>
          <Link
            to="/studio/reels"
            className="studio-btn-ghost no-underline inline-flex"
            data-testid="studio-progress-go-reels"
          >
            {STUDIO_COPY.reelsLeaveCta}
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default StudioProgressPanel;
